import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Alert,
  Button,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Print as PrintIcon } from '@mui/icons-material';
import { useReturnInvoice, useSales } from '../services/api';
import { resolveImageUrl } from '../utils/imageUrl';
import { printReturnInvoices } from '../utils/print';

const getInvoiceItems = (invoice) => {
  if (!invoice) return [];
  return invoice.items ?? invoice.returnInvoiceItems ?? [];
};

const itemField = (item, camel, snake) =>
  item[camel] ?? item[snake] ?? '';

const ReturnInvoiceDetailPage = () => {
  const { saleId, invoiceId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data: invoice, isLoading, isError, error } = useReturnInvoice(invoiceId);
  const { data: sales = [] } = useSales();
  const sale = sales.find((s) => String(s.id) === String(saleId));

  const allItems = useMemo(() => getInvoiceItems(invoice), [invoice]);
  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage;
    return allItems.slice(start, start + rowsPerPage);
  }, [allItems, page, rowsPerPage]);

  const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleString();
  };

  const formatMoney = (value) => `$${parseFloat(value ?? 0).toFixed(2)}`;

  const handleBack = () => {
    navigate(`/sales/${saleId}/return-invoice`);
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <LinearProgress sx={{ mt: 4 }} />
      </Container>
    );
  }

  if (isError || !invoice) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <IconButton onClick={handleBack} aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
          <Alert severity="error" sx={{ mt: 2 }}>
            {error?.response?.data?.message || 'Return invoice not found'}
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={handleBack} aria-label="Back to return invoices">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" component="h1">
                Returned Items
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Invoice {String(invoice.id).slice(0, 8)}…
                {sale && ` · ${sale.user?.fullName || sale.code}`}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            color="info"
            startIcon={<PrintIcon />}
            onClick={() =>
              printReturnInvoices(
                [invoice],
                sale ? `Sales rep: ${sale.user?.fullName || sale.code}` : ''
              )
            }
          >
            Print
          </Button>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total amount
                </Typography>
                <Typography variant="body1">
                  {formatMoney(invoice.totalAmount)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Return amount
                </Typography>
                <Typography variant="body1" color="primary">
                  {formatMoney(invoice.totalReturnAmount)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body1">{formatDate(invoice.createdAt)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  All items returned
                </Typography>
                <Chip
                  size="small"
                  label={invoice.isAllItemsReturned ? 'Yes' : 'No'}
                  color={invoice.isAllItemsReturned ? 'success' : 'default'}
                />
              </Grid>
              {invoice.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Notes
                  </Typography>
                  <Typography variant="body1">{invoice.notes}</Typography>
                </Grid>
              )}
              {invoice.adminNotes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Admin notes
                  </Typography>
                  <Typography variant="body1">{invoice.adminNotes}</Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Box sx={{ px: 2, pt: 2 }}>
              <Typography variant="h6">
                Items ({allItems.length})
              </Typography>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, mx: 0 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Trademark</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Returned qty</TableCell>
                    <TableCell align="right">Unit price</TableCell>
                    <TableCell align="right">Cost</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell>Return reason</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        No items on this invoice
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedItems.map((item) => {
                      const productName = itemField(item, 'productName', 'product_name');
                      const productNameArabic = itemField(item, 'productNameArabic', 'product_name_arabic');
                      const imageUrl = itemField(item, 'productImageUrl', 'product_image_url');
                      const quantity = item.quantity ?? 0;
                      const returnedQty = item.returnedQuantity ?? item.returned_quantity ?? '—';
                      const isReturned = item.isReturned ?? item.is_returned;

                      return (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              {imageUrl && (
                                <Box
                                  component="img"
                                  src={resolveImageUrl(imageUrl)}
                                  alt={productName}
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                  }}
                                />
                              )}
                              <Box>
                                <Typography variant="body2">{productName || '—'}</Typography>
                                {productNameArabic && (
                                  <Typography variant="caption" color="text.secondary">
                                    {productNameArabic}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {itemField(item, 'categoryName', 'category_name') || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {itemField(item, 'trademarkName', 'trademark_name') || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{quantity}</TableCell>
                          <TableCell align="right">{returnedQty}</TableCell>
                          <TableCell align="right">
                            {formatMoney(item.unitPrice ?? item.unit_price)}
                          </TableCell>
                          <TableCell align="right">
                            {formatMoney(item.cost)}
                          </TableCell>
                          <TableCell align="right">
                            {formatMoney(item.discount)}
                          </TableCell>
                          <TableCell>
                            {item.returnReason || item.return_reason || '—'}
                          </TableCell>
                          <TableCell>
                            {isReturned === undefined ? (
                              '—'
                            ) : (
                              <Chip
                                size="small"
                                label={isReturned ? 'Returned' : 'Pending'}
                                color={isReturned ? 'success' : 'warning'}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              {allItems.length > 0 && (
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  count={allItems.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              )}
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

    </Container>
  );
};

export default ReturnInvoiceDetailPage;
